import { TextField } from "@kobalte/core/text-field"
import { blogUrl } from "@repo/fika-blog/src/blogUrl.js"
import {
  bookmarkHeaderClass,
  bookmarkHeaderDescriptionClass,
  bookmarkHeaderIconClass,
  bookmarkHeaderIconSvgClass,
  bookmarkHeaderImageClass,
  bookmarkHeaderLinkClass,
  bookmarkHeaderLinkTextClass,
  bookmarkHeaderTitleClass,
} from "@repo/fika-blog/src/bookmarkHeader.css"
import { hasFocusClass, postContentClass } from "@repo/fika-blog/src/classes"
import { postClass, postContainerClass } from "@repo/fika-blog/src/content.css"
import { extensions } from "@repo/fika-blog/src/extensions"
import { NodeTypeExtension } from "@repo/fika-blog/src/extensions/nodeType"
import {
  postDescriptionClass,
  postHeaderClass,
  postHeaderContentClass,
  postHeaderIconClass,
  postHeaderIconSvgClass,
  postTitleClass,
} from "@repo/fika-blog/src/postHeader.css"
import type { JSONContent } from "@repo/fika-blog/src/tiptap/core"
import type { Bookmark } from "@repo/fika-shared/src/loader/bookmark-schema.js"
import type { Post } from "@repo/fika-shared/src/loader/post-schema.js"
import { useConnectivitySignal } from "@solid-primitives/connectivity"
import { debounce } from "@solid-primitives/scheduled"
import TiptapFileHandler from "@tiptap/extension-file-handler"
import Focus from "@tiptap/extension-focus"
import TiptapUniqueID from "@tiptap/extension-unique-id"
import { isDeepEqual, isEmpty } from "remeda"
import {
  createEffect,
  createMemo,
  createSignal,
  Match,
  onCleanup,
  Show,
  Switch,
  untrack,
} from "solid-js"
import { createTiptapEditor, useEditorJSON } from "solid-tiptap"
import { ulid } from "ulid"
import { useAppContext } from "@/context/app"
import { db } from "@/data/database"
import { Box } from "@/designSystem/components/box"
import { Button, LinkButton } from "@/designSystem/components/button"
import { Icon } from "@/designSystem/components/icon"
import { Text } from "@/designSystem/components/text"
import { MainLayout } from "@/designSystem/layouts/mainLayout"
import { ButtonDrawer } from "@/designSystem/patterns/button-drawer"
import { ImageUpload } from "@/designSystem/patterns/image-upload"
import { Navigation } from "@/designSystem/patterns/navigation"
import { Pills } from "@/designSystem/patterns/pills"
import { postStatusPill } from "@/designSystem/patterns/post"
import { TagWithSuggestions } from "@/designSystem/patterns/tags-with-suggestions"
import { useI18nContext } from "@/i18n/i18n-solid"
import { defaultJsonContent } from "@/lib/defaultJsonContent"
import { getTitle } from "@/lib/getTitle"
import { generateUrl } from "@/lib/slug"
import { createBlogRouter } from "@/routes/posts-router"
import { PanelMenu } from "./editor/menus/panelMenu"
import { Placeholder } from "./editor/nodes/placeholder"
import { RightPanel } from "./editor/rightPanel"
import { onDrop, onPaste } from "./image-upload"
import {
  clickAreaClass,
  dropCursorClass,
  editorClass,
  editorWrapperClass,
  statusRecipe,
  textFieldClass,
} from "./post-item.css"
import usePost from "./use-post"

const FIKA_PUBLIC_URL = import.meta.env.VITE_FIKA_PUBLIC_URL

export function PostItem() {
  const post = usePost()

  return <Show when={post()}>{post => <SafePostItem post={post()} />}</Show>
}

function SafePostItem(props: { post: Post }) {
  const { user, replicache } = useAppContext()
  const [formTitle, setFormTitle] = createSignal(props.post.title || "")
  const [formDescription, setFormDescription] = createSignal(
    props.post.description,
  )
  const postVersions = createMemo(() =>
    db.post.filterBy({ root_id: props.post.id }),
  )
  const publishedVersion = createMemo(() =>
    postVersions().find(post => post.published_at),
  )

  let editorRef!: HTMLDivElement

  const postJSON = createMemo<JSONContent>(
    () => JSON.parse(props.post.json || defaultJsonContent),
    {},
    { equals: isDeepEqual },
  )

  const getEditor = createTiptapEditor(() => ({
    element: editorRef,
    content: untrack(() => postJSON()),
    editorProps: {
      attributes: {
        class: `${editorClass} ${postContentClass}`,
      },
    },
    extensions: [
      ...extensions({
        dropcursor: {
          // @ts-ignore
          color: false,
          width: 2,
          class: dropCursorClass,
        },
      }),
      TiptapUniqueID.configure({
        attributeName: "uid",
        types: ["image"],
      }),
      Placeholder,
      Focus.configure({ className: hasFocusClass, mode: "deepest" }),
      TiptapFileHandler.configure({ onDrop, onPaste }),
      NodeTypeExtension,
    ],
  }))

  const getJSON = useEditorJSON(() => getEditor())

  function addParagraph() {
    const editor = getEditor()
    if (!editor) return

    const lastPos = editor.state.doc.content.size
    editor.chain().focus().insertContentAt(lastPos, { type: "paragraph" }).run()
  }

  function handleKeyDown(event: KeyboardEvent) {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault()
      addParagraph()
    }
  }

  createEffect(() => {
    const editor = getEditor()
    if (!editor) return

    const json = getJSON()
    if (isDeepEqual(postJSON(), json)) return

    const imgPositions: { pos: number; attributes: any }[] = []
    editor.state.doc.descendants((node, pos) => {
      if (node.type.name === "image") {
        imgPositions.push({
          pos,
          attributes: node.attrs,
        })
      }
    })

    const imgs: Record<string, string> = {}

    function traverse(node: any) {
      if (node.type === "image") {
        imgs[node.attrs.uid] = node.attrs.src
      }

      if (node.content && Array.isArray(node.content)) {
        node.content.forEach(traverse)
      }
    }

    traverse(postJSON())

    for (const pos of imgPositions) {
      const src = imgs[pos.attributes.uid]
      if (!src) continue
      if (src === pos.attributes.src) continue

      // https://x.com/rauchg/status/1805274417158824431
      const img = new Image()
      img.onload = () => {
        const oldSelection = editor.state.selection
        editor
          .chain()
          .setNodeSelection(pos.pos)
          .updateAttributes("image", { src })
          .setTextSelection(oldSelection.from)
          .run()
      }
      img.src = src
    }
  })

  const getBookmark = createMemo(() =>
    db.bookmark.get(props.post.bookmark_id ?? null),
  )
  const getUserName = createMemo(() => user.name)
  const url = createMemo(() =>
    generateUrl(getTitle(props.post.title), props.post.id),
  )

  const { paths } = createBlogRouter()
  const isOnline = useConnectivitySignal()
  const sentVersion = createMemo(() =>
    postVersions().some(post => post.sent_at),
  )

  const { LL } = useI18nContext()
  const buttons = createMemo(() => {
    return [
      {
        label: LL().common.blog(),
        href: paths().url,
      } as const,
    ]
  })

  function checkDirty(post: Post) {
    const json = getJSON()
    if (!json) return false

    const jsonString = json ? JSON.stringify(json) : defaultJsonContent
    const titleDirty = formTitle() !== post.title
    const descriptionDirty = formDescription() !== post.description
    const jsonDirty = jsonString !== post.json

    return titleDirty || descriptionDirty || jsonDirty
  }

  const isDirty = createMemo(() => checkDirty(props.post))
  const isPublishable = createMemo(() => {
    if (isEmpty(formTitle())) return false

    const json = getJSON()
    if (JSON.stringify(json) === defaultJsonContent) return false

    const post = publishedVersion()
    if (!post) return true

    return checkDirty(post)
  })

  const debouncedCreateVersion = debounce(createVersion, 10_000)

  async function createVersion(post: Post) {
    console.log("CREATE VERSION CALLED")
    const versionId = ulid()

    await replicache.mutate.versionPost({
      id: post.id,
      version_id: versionId,
      title: formTitle(),
      description: formDescription(),
      json: JSON.stringify(getJSON()),
    })
  }

  async function publish() {
    if (isDirty() && isPublishable()) {
      debouncedCreateVersion.clear()
      await createVersion(props.post)
    }

    await replicache.mutate.publishPost({
      id: props.post.id,
      published_at: new Date().toISOString(),
      sent_at: null,
    })
  }

  createEffect(() => {
    // don't delete, it's for tracking purposes
    const tracking = [getJSON(), formTitle(), formDescription(), isDirty()]
    if (!isDirty()) return
    console.log("effect of debouncedCreateVersion")

    debouncedCreateVersion(props.post)
    return tracking
  })

  const getPost = createMemo(() => props.post)

  onCleanup(() => {
    if (!isDirty()) return
    createVersion(getPost())
  })
  const tagOptions = createMemo(() =>
    db.tag.filterBy({ target_type: "post" }).map(t => t.tag),
  )

  const getTags = createMemo(() => {
    return new Set(
      db.tag
        .filterBy({ target_id: props.post.id, target_type: "post" })
        .map(t => t.tag),
    )
  })

  return (
    <MainLayout
      header={
        <Box gap="12">
          <Box
            alignItems="center"
            justifyContent="space-between"
            flexDirection="row"
            gap="24"
          >
            <Navigation
              hue="amber"
              buttons={buttons()}
              title={isEmpty(formTitle()) ? "Untitled" : formTitle()}
            />
            <Show when={getUserName()}>
              {userName => (
                <LinkButton
                  hue="amber"
                  kind="ghost"
                  iconName="external-link"
                  target="_blank"
                  href={`${blogUrl(FIKA_PUBLIC_URL, userName())}/${url()}`}
                >
                  preview
                </LinkButton>
              )}
            </Show>
          </Box>
          <PanelMenu editor={getEditor()} />
          <Switch
            fallback={
              <div class={statusRecipe({ hue: "sand" })}>
                <Box gap="12" flexDirection="row" alignItems="flex-start">
                  <Pills pills={[postStatusPill(props.post)]} />
                  <Text color="sand800" fontSize="m">
                    this is a draft version, you can preview it but it's not
                    visible to your readers
                  </Text>
                </Box>
              </div>
            }
          >
            <Match when={props.post.sent_at}>
              <div class={statusRecipe({ hue: "indigo" })}>
                <Box gap="12" flexDirection="row" alignItems="flex-start">
                  <Pills pills={[postStatusPill(props.post)]} />
                  <Text color="indigo800" fontSize="m">
                    this version was sent to your email subscribers
                  </Text>
                </Box>
              </div>
            </Match>
            <Match when={props.post.published_at}>
              <div class={statusRecipe({ hue: "emerald" })}>
                <Box gap="12" flexDirection="row" alignItems="flex-start">
                  <Pills pills={[postStatusPill(props.post)]} />
                  <Text color="emerald800" fontSize="m">
                    this version is published and visible to your readers
                  </Text>
                </Box>
              </div>
            </Match>
            <Match when={props.post.archived_at}>
              <div class={statusRecipe({ hue: "orange" })}>
                <Box gap="12" flexDirection="row" alignItems="flex-start">
                  <Pills pills={[postStatusPill(props.post)]} />
                  <Text color="orange700" fontSize="m">
                    this version is archived, people can't see it, but you can
                    still edit it.
                  </Text>
                </Box>
              </div>
            </Match>
          </Switch>
        </Box>
      }
      footer={
        <ButtonDrawer
          hue="amber"
          leftActions={
            <>
              <Show when={isOnline()}>
                <LinkButton
                  kind="darkGhost"
                  iconName="history"
                  hue="amber"
                  href={paths().post().versions()}
                />
              </Show>
              <LinkButton
                kind="darkGhost"
                iconName="settings"
                hue="amber"
                href={paths().post().settings()}
              >
                settings
              </LinkButton>
            </>
          }
          rightActions={
            <Switch>
              <Match when={props.post.archived_at}>
                <Button
                  hue="emerald"
                  iconName="archive"
                  onClick={() => {
                    replicache.mutate.archivePost({
                      id: props.post.id,
                      archived_at: null,
                    })
                  }}
                >
                  unarchive
                </Button>
              </Match>
              <Match when={isPublishable()}>
                <Show
                  when={sentVersion()}
                  fallback={
                    <LinkButton
                      hue="indigo"
                      iconName="send"
                      href={paths().post(props.post).publish()}
                    >
                      publish & send
                    </LinkButton>
                  }
                >
                  <Button hue="emerald" iconName="book-up" onClick={publish}>
                    update
                  </Button>
                </Show>
              </Match>
            </Switch>
          }
        />
      }
      rightPanel={
        <Show when={getEditor()}>
          {editor => <RightPanel editor={editor()} bookmark={getBookmark()} />}
        </Show>
      }
    >
      <div class={postContainerClass}>
        <div class={postClass}>
          <Show
            when={getBookmark()}
            fallback={
              <PostHeader
                post={props.post}
                formTitle={formTitle()}
                formDescription={formDescription() ?? ""}
                setFormTitle={setFormTitle}
                setFormDescription={setFormDescription}
              />
            }
          >
            {bookmark => (
              <BookmarkHeader
                post={props.post}
                bookmark={bookmark()}
                formTitle={formTitle()}
                setFormTitle={setFormTitle}
              />
            )}
          </Show>
          <Box gap="16">
            <TagWithSuggestions
              options={tagOptions()}
              tags={getTags()}
              suggestions={null}
              targetType="post"
              targetId={props.post.id}
              hue="amber"
            />
            <div ref={editorRef} class={editorWrapperClass} />
            <Show when={!getEditor()?.isEmpty}>
              <div
                class={clickAreaClass}
                onClick={addParagraph}
                onKeyDown={handleKeyDown}
                onFocus={addParagraph}
                role="button"
                tabIndex={0}
              />
            </Show>
          </Box>
        </div>
      </div>
    </MainLayout>
  )
}

type PostHeaderProps = {
  post: Post
  formTitle: string
  setFormTitle: (title: string) => void
  formDescription: string
  setFormDescription: (description: string) => void
}
function PostHeader(props: PostHeaderProps) {
  return (
    <div class={postHeaderClass}>
      <HeaderImage post={props.post} type="post" />
      <div class={postHeaderContentClass}>
        <div class={postHeaderIconClass}>
          <Icon
            class={postHeaderIconSvgClass}
            color="amber600"
            name="pen-tool"
            size="32"
          />
        </div>
        <TextField
          value={props.formTitle}
          onChange={props.setFormTitle}
          class={textFieldClass}
        >
          <TextField.TextArea
            rows="1"
            placeholder="title"
            class={postTitleClass}
            autoResize
          />
        </TextField>
        <TextField
          value={props.formDescription}
          onChange={props.setFormDescription}
          class={textFieldClass}
        >
          <TextField.TextArea
            placeholder="subtitle"
            class={postDescriptionClass}
            rows="1"
            autoResize
          />
        </TextField>
      </div>
    </div>
  )
}

type BookmarkHeaderProps = {
  post: Post
  bookmark: Bookmark
  formTitle: string
  setFormTitle: (title: string) => void
}
function BookmarkHeader(props: BookmarkHeaderProps) {
  const { db } = useAppContext()
  const getStory = createMemo(() => db.story.get(props.bookmark.story_id))

  return (
    <div class={postHeaderClass}>
      <TextField
        value={props.formTitle}
        onChange={props.setFormTitle}
        class={textFieldClass}
      >
        <TextField.TextArea
          rows="1"
          placeholder="title"
          class={postTitleClass}
          autoResize
        />
      </TextField>
      <div class={postHeaderContentClass}>
        <div class={bookmarkHeaderIconClass}>
          <Icon
            class={bookmarkHeaderIconSvgClass}
            color="amber600"
            name="book-text"
            size="24"
          />
        </div>
        <Show when={getStory()}>
          {story => (
            <div class={bookmarkHeaderClass}>
              <Show when={story().image_url}>
                {imageUrl => (
                  <img
                    src={imageUrl()}
                    alt={props.formTitle}
                    class={bookmarkHeaderImageClass}
                  />
                )}
              </Show>
              <a
                href={story()?.url}
                target="_blank"
                rel="noreferrer"
                class={bookmarkHeaderLinkClass}
              >
                <Icon name="external-link" color="sand700" size="18" />
                <span class={bookmarkHeaderLinkTextClass}>{story().url}</span>
              </a>
              <span class={bookmarkHeaderTitleClass}>
                {getTitle(story().dc_title)}
              </span>
              <span class={bookmarkHeaderDescriptionClass}>
                {story().summary ?? story().dc_description}
              </span>
            </div>
          )}
        </Show>
      </div>
    </div>
  )
}

function HeaderImage(props: { post: Post; type: "post" | "bookmark" }) {
  const { replicache } = useAppContext()

  function onUpload(src: string | null) {
    replicache.mutate.updatePost({
      id: props.post.id,
      image_header_url: src,
    })
  }

  return (
    <ImageUpload
      text="upload header image (16:9, 800 x 450 pixels)"
      imageSrc={props.post.image_header_url}
      onUpload={onUpload}
      hue="amber"
    />
  )
}
